import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { useAuth } from '@/auth/useAuth';
import { deriveUserRole, type UserRole } from './useCourse';

export interface RawDateMessage {
  day?: number | null;
  month?: number | null;
  year?: number | null;
}

export interface RawUserMessage {
  display_name?: string | null;
  displayName?: string | null;
  email?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  name?: string | null;
  preferred_email?: string | null;
  preferredEmail?: string | null;
}

interface RawCourseComponent {
  end_time_utc_milli?: number | string | null;
  endTimeUtcMilli?: number | string | null;
  id?: string | null;
  is_section_divider?: boolean | null;
  isSectionDivider?: boolean | null;
  pre_lecture_end_time_utc_milli?: number | string | null;
  preLectureEndTimeUtcMilli?: number | string | null;
  title?: string | null;
  uid?: string | null;
}

export interface RawCourse {
  archived_course_ids?: string[] | null;
  archivedCourseIds?: string[] | null;
  banner_url?: string | null;
  bannerUrl?: string | null;
  course_ids?: string[] | null;
  courseIds?: string[] | null;
  component_uids?: string[] | null;
  componentUids?: string[] | null;
  components?: RawCourseComponent[] | null;
  end_date?: RawDateMessage | null;
  id?: string | null;
  instructor?: RawUserMessage | null;
  is_complete?: boolean | null;
  is_live?: boolean | null;
  last_updated_time_seconds?: number | string | null;
  lastUpdatedTimeSeconds?: number | string | null;
  name?: string | null;
  parent_subject?: unknown;
  start_date?: RawDateMessage | null;
  user?: RawCourse | null;
  uid?: string | null;
}

export type RawCoursesResponse =
  | RawCourse[]
  | Record<string, unknown>
  | null
  | undefined;

export interface CourseListItem {
  bannerUrl: string | null;
  creationIndex: number;
  href: string;
  iconLabel: string;
  iconVariant: 'subject' | 'generic';
  id: string;
  isArchived: boolean;
  instructorName: string;
  isComplete?: boolean;
  isLive?: boolean;
  lastUpdatedTimeSeconds: number | null;
  nextUpDueLabel: string | null;
  nextUpTitle: string | null;
  parentSubject: string | null;
  subtitle: string;
  termLabel: string;
  title: string;
  userRole?: UserRole;
}

export type CourseListItems = CourseListItem[] & {
  userCourseIds: string[];
};

export const COURSES_QUERY_KEY = ['courses'] as const;
export const coursesQueryKey = (userId: string | null | undefined) =>
  [...COURSES_QUERY_KEY, userId ?? 'anonymous'] as const;
const GROUPED_COURSE_KEYS = [
  'allCourse',
  'all_course',
  'allCourses',
  'all_courses',
  'liveCourse',
  'live_course',
  'liveCourses',
  'live_courses',
  'pastCourse',
  'past_course',
  'pastCourses',
  'past_courses',
  'otherCourse',
  'other_course',
  'otherCourses',
  'other_courses',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isCourse(value: unknown): value is RawCourse {
  return isRecord(value);
}

function toCourseArray(value: unknown): RawCourse[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isCourse);
}

function toCourseComponentArray(value: unknown): RawCourseComponent[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawCourseComponent[];
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeString(entry))
    .filter((entry): entry is string => entry !== null);
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function getDisplayName(user: RawUserMessage | null | undefined): string | null {
  if (!user) {
    return null;
  }

  const firstName = normalizeString(user.first_name) ?? normalizeString(user.firstName);
  const lastName = normalizeString(user.last_name) ?? normalizeString(user.lastName);

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return (
    normalizeString(user.name) ??
    normalizeString(user.displayName) ??
    normalizeString(user.display_name) ??
    firstName ??
    lastName ??
    normalizeString(user.preferred_email) ??
    normalizeString(user.preferredEmail) ??
    normalizeString(user.email)
  );
}

function getSubjectLabel(parentSubject: unknown): string | null {
  if (typeof parentSubject === 'string') {
    return normalizeString(parentSubject);
  }

  if (!isRecord(parentSubject)) {
    return null;
  }

  return (
    normalizeString(parentSubject.name) ??
    normalizeString(parentSubject.title) ??
    normalizeString(parentSubject.shortName) ??
    normalizeString(parentSubject.short_name) ??
    normalizeString(parentSubject.code)
  );
}

function getInitials(source: string): string {
  const parts = source
    .split(/[\s-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return 'CU';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

function formatNextDueDate(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
  }).format(new Date(timestamp));
}

export function deriveTermLabel(date?: RawDateMessage | null): string {
  if (!date?.year || !date?.month) {
    return 'Term TBD';
  }

  let season = 'Term TBD';

  if (date.month >= 1 && date.month <= 3) {
    season = 'Winter';
  } else if (date.month >= 4 && date.month <= 5) {
    season = 'Spring';
  } else if (date.month >= 6 && date.month <= 8) {
    season = 'Summer';
  } else if (date.month >= 9 && date.month <= 12) {
    season = 'Fall';
  }

  return season === 'Term TBD' ? season : `${season} ${date.year}`;
}

function getFlatCourseArray(response: RawCoursesResponse): RawCourse[] {
  if (Array.isArray(response)) {
    return response.filter(isCourse);
  }

  if (!isRecord(response)) {
    return [];
  }

  const allCourses = toCourseArray(
    response.allCourse ??
      response.all_course ??
      response.allCourses ??
      response.all_courses,
  );

  if (allCourses.length > 0) {
    return allCourses;
  }

  const groupedCourses = [
    ...toCourseArray(
      response.liveCourse ?? response.live_course ?? response.liveCourses ?? response.live_courses,
    ),
    ...toCourseArray(
      response.pastCourse ?? response.past_course ?? response.pastCourses ?? response.past_courses,
    ),
    ...toCourseArray(
      response.otherCourse ??
        response.other_course ??
        response.otherCourses ??
        response.other_courses,
    ),
  ];

  if (groupedCourses.length > 0) {
    return groupedCourses;
  }

  const courses: RawCourse[] = [];
  const knownKeys = new Set<string>(GROUPED_COURSE_KEYS);

  for (const [key, value] of Object.entries(response)) {
    if (knownKeys.has(key)) {
      continue;
    }

    courses.push(...toCourseArray(value));
  }

  return courses;
}

function orderCourseComponents(course: RawCourse): RawCourseComponent[] {
  const components = toCourseComponentArray(course.components);
  const orderedUids = toStringArray(course.component_uids ?? course.componentUids);

  if (orderedUids.length === 0) {
    return components;
  }

  const remainingComponents = [...components];
  const orderedComponents: RawCourseComponent[] = [];

  for (const orderedUid of orderedUids) {
    const componentIndex = remainingComponents.findIndex((component) => {
      const componentUid = normalizeString(component.uid);
      const componentId = normalizeString(component.id);

      return componentUid === orderedUid || componentId === orderedUid;
    });

    if (componentIndex >= 0) {
      orderedComponents.push(...remainingComponents.splice(componentIndex, 1));
    }
  }

  return [...orderedComponents, ...remainingComponents];
}

function getNextUpDetails(course: RawCourse): {
  nextUpDueLabel: string | null;
  nextUpTitle: string | null;
} {
  const now = Date.now();

  for (const component of orderCourseComponents(course)) {
    const isSectionDivider =
      typeof component.is_section_divider === 'boolean'
        ? component.is_section_divider
        : typeof component.isSectionDivider === 'boolean'
          ? component.isSectionDivider
          : false;

    if (isSectionDivider) {
      continue;
    }

    const title = normalizeString(component.title) ?? 'Untitled assignment';
    const dueCandidates = [
      normalizeNumber(
        component.pre_lecture_end_time_utc_milli ??
          component.preLectureEndTimeUtcMilli,
      ),
      normalizeNumber(component.end_time_utc_milli ?? component.endTimeUtcMilli),
    ]
      .filter((value): value is number => value !== null)
      .filter((value) => value > now)
      .sort((left, right) => left - right);

    if (dueCandidates.length === 0) {
      continue;
    }

    return {
      nextUpDueLabel: `Due: ${formatNextDueDate(dueCandidates[0]!)}`,
      nextUpTitle: title,
    };
  }

  return {
    nextUpDueLabel: null,
    nextUpTitle: null,
  };
}

function getArchivedCourseIds(response: RawCoursesResponse): Set<string> {
  if (!isRecord(response)) {
    return new Set();
  }

  const rawUser = isRecord(response.user) ? (response.user as RawCourse) : null;
  const archivedIds = [
    ...(Array.isArray(response.archived_course_ids)
      ? response.archived_course_ids
      : Array.isArray(response.archivedCourseIds)
        ? response.archivedCourseIds
        : []),
    ...(Array.isArray(rawUser?.archived_course_ids)
      ? rawUser.archived_course_ids
      : Array.isArray(rawUser?.archivedCourseIds)
        ? rawUser.archivedCourseIds
        : []),
  ]
    .map((value) => normalizeString(value))
    .filter((value): value is string => value !== null);

  return new Set(archivedIds);
}

export function getUserCourseIds(response: RawCoursesResponse): string[] {
  if (!isRecord(response)) {
    return [];
  }

  const rawUser = isRecord(response.user) ? (response.user as RawCourse) : null;
  return toStringArray(
    response.course_ids ??
      response.courseIds ??
      rawUser?.course_ids ??
      rawUser?.courseIds,
  );
}

function attachUserCourseIds(
  courses: CourseListItem[],
  userCourseIds: string[],
): CourseListItems {
  Object.defineProperty(courses, 'userCourseIds', {
    configurable: true,
    enumerable: false,
    value: userCourseIds,
    writable: false,
  });

  return courses as CourseListItems;
}

function normalizeCourse(
  course: RawCourse,
  index: number,
  archivedCourseIds: Set<string>,
  currentUser?: Parameters<typeof deriveUserRole>[1],
): CourseListItem {
  const id =
    normalizeString(course.id) ??
    normalizeString(course.uid) ??
    `course-${index + 1}`;
  const title = normalizeString(course.name) ?? 'Untitled course';
  const subjectLabel = getSubjectLabel(course.parent_subject);
  const termFromStart = deriveTermLabel(course.start_date);
  const termLabel =
    termFromStart !== 'Term TBD'
      ? termFromStart
      : deriveTermLabel(course.end_date);
  const lastUpdatedTimeSeconds = normalizeNumber(
    course.last_updated_time_seconds ?? course.lastUpdatedTimeSeconds,
  );
  const { nextUpDueLabel, nextUpTitle } = getNextUpDetails(course);
  const bannerUrl =
    normalizeString(course.banner_url) ?? normalizeString(course.bannerUrl);

  return {
    bannerUrl,
    creationIndex: index,
    href: `/app/courses/${id}`,
    iconLabel: getInitials(subjectLabel ?? title),
    iconVariant: subjectLabel ? 'subject' : 'generic',
    id,
    isArchived: archivedCourseIds.has(id),
    instructorName: getDisplayName(course.instructor) ?? 'Course staff',
    isComplete:
      typeof course.is_complete === 'boolean' ? course.is_complete : undefined,
    isLive: typeof course.is_live === 'boolean' ? course.is_live : undefined,
    lastUpdatedTimeSeconds: lastUpdatedTimeSeconds ?? null,
    nextUpDueLabel,
    nextUpTitle,
    parentSubject: subjectLabel,
    subtitle: subjectLabel ?? id,
    termLabel,
    title,
    ...(currentUser ? { userRole: deriveUserRole(course as never, currentUser) } : {}),
  };
}

export function normalizeCourseResponse(
  response: RawCoursesResponse,
  currentUser?: Parameters<typeof deriveUserRole>[1],
): CourseListItems {
  const archivedCourseIds = getArchivedCourseIds(response);
  const userCourseIds = getUserCourseIds(response);

  return attachUserCourseIds(
    getFlatCourseArray(response).map((course, index) =>
      normalizeCourse(course, index, archivedCourseIds, currentUser),
    ),
    userCourseIds,
  );
}

export function unwrapCoursesResponse(
  response:
    | RawCoursesResponse
    | LegacyKuduResponseEnvelope<RawCoursesResponse>,
): RawCoursesResponse {
  if (isRecord(response) && 'Response' in response) {
    return response.Response as RawCoursesResponse;
  }

  return response as RawCoursesResponse;
}

export function useCourses(): UseQueryResult<CourseListItems, ApiError> {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, loading, user } = useAuth();
  const userId = getKuduUserId(user) ?? user?.uid ?? null;

  return useQuery<CourseListItems, ApiError>({
    enabled: !loading && Boolean(user) && Boolean(userId),
    queryFn: async () => {
      if (!user || !userId) {
        throw new Error('Authenticated user is required to load courses.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        // TODO: Replace the legacy MD5 provider-UID token flow when the
        // backend supports Firebase ID token verification.
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      const response = await client.invokeKuduMethod<
        RawCoursesResponse | LegacyKuduResponseEnvelope<RawCoursesResponse>,
        { user_id: string }
      >('GetCourses', { user_id: userId });
      const unwrappedResponse = unwrapCoursesResponse(response);

      // TODO: Verify the exact API response envelope once real wiring and
      // protobuf-ts generated response types are in place.
      return normalizeCourseResponse(unwrappedResponse, user);
    },
    queryKey: coursesQueryKey(userId),
    retry: false,
  });
}
